// ===============================
// src/OngletGardes.jsx
// ===============================
// Interface Responsable – Gestion des gardes (HUB)
//  - Gardes du mois en cours  → vue liste validée (/responsable/gardes-valide?mois=YYYY-MM)
//  - Gardes – désidérata      → page dédiée /responsable/gardes-desiderata
//  - Gardes des mois précédents → sélecteur (archives / accès listes)
//  - Gardes des mois à venir   → sélecteur (accès listes)
// ===============================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import LogoutButton from "./components/LogoutButton.jsx";
import { goContextualBack } from "./contextualBack.js";
import { withResponsibleBridgeContext } from "./responsableBack.js";
import { apiFetch } from "./apiMedecins.js";
import { useAuth } from "./contexts/AuthContext.jsx";
import { canManageJuniorCampaign } from "./components/JuniorCampaignAccess.jsx";

export default function OngletGardes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFromUrl = searchParams.get("type"); // ex. ASTREINTE_THORACIQUE, PR1, PR2
  const lockSecteur = searchParams.get("lockSecteur") === "1";
  const [typeGardeState, setTypeGardeState] = useState(() => ["PR1", "PR2", "DIGESTIF", "ORTHOPEDIE", "ORTHOPEDIE_JUNIOR", "INTERNE1", "INTERNE2", "REANIMATION", "INTERNE_DIGESTIF", "INTERNE_REA"].includes(typeFromUrl) ? typeFromUrl : "PR2");
  // Type effectif : URL prime (secteur dédié), sinon state du dropdown
  const typeGarde = typeFromUrl || typeGardeState;
  const isDirectSaisieType = ["ORTHOPEDIE", "ORTHOPEDIE_JUNIOR"].includes(typeGarde);
  const isJuniorType = ["INTERNE1", "INTERNE2", "INTERNE_DIGESTIF", "INTERNE_REA", "ORTHOPEDIE_JUNIOR"].includes(typeGarde);
  const showPilotage = (!isDirectSaisieType || typeGarde === "ORTHOPEDIE") &&
    (!isJuniorType || canManageJuniorCampaign(user, typeGarde));
  const juniorCampaign = {
    DIGESTIF: { type: "INTERNE_DIGESTIF", label: "Campagne des internes Digestif" },
    REANIMATION: { type: "INTERNE_REA", label: "Campagne des internes Réanimation" },
  }[typeGarde];
  const isAstreinteType = ["ASTREINTE_THORACIQUE", "ORTHOPEDIE", "ORTHOPEDIE_JUNIOR"].includes(typeGarde);
  const hideListeMedecinsBlock = ["PR1", "PR2", "DIGESTIF", "REANIMATION", "ORTHOPEDIE", "ASTREINTE_THORACIQUE"].includes(typeGarde);

  const withScopedLock = (path) => {
    if (!lockSecteur) return path;
    const sep = path.includes("?") ? "&" : "?";
    return `${path}${sep}lockSecteur=1`;
  };

  const withCurrentBack = (path) => {
    return withResponsibleBridgeContext(path, { backMode: "current" });
  };

  // Garder l’URL en phase avec le type (PR1/PR2) pour que la page "Liste des médecins" reçoive toujours le bon type
  useEffect(() => {
    if (["PR1", "PR2", "DIGESTIF", "ORTHOPEDIE", "ORTHOPEDIE_JUNIOR", "INTERNE1", "INTERNE2", "REANIMATION", "INTERNE_DIGESTIF", "INTERNE_REA"].includes(typeFromUrl)) {
      setTypeGardeState(typeFromUrl);
    }
  }, [typeFromUrl]);

  // Quand on est sur Gardes sans type dans l’URL (ex. clic sur "Gardes Port Royal"), mettre ?type=PR2 par défaut
  useEffect(() => {
    const t = typeFromUrl ?? "";
    if (t === "" && typeGardeState) {
      setSearchParams({ type: typeGardeState }, { replace: true });
    }
  }, [typeFromUrl, typeGardeState, setSearchParams]);
  const [cfg, setCfg] = useState(null);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [cfgError, setCfgError] = useState(null);

  // ✅ Modales mois
  const [showMoisModal, setShowMoisModal] = useState(false);
  const [moisModalMode, setMoisModalMode] = useState("avenir"); // "avenir" | "precedents"
  const [moisItems, setMoisItems] = useState([]); // [{ mois, label, hasValidee }]
  const [moisLoading, setMoisLoading] = useState(false);
  const [moisErr, setMoisErr] = useState(null);

  useEffect(() => {
    const loadCfg = async () => {
      try {
        if (!showPilotage) {
          setCfg(null);
          setCfgLoading(false);
          setCfgError(null);
          return;
        }
        setCfgLoading(true);
        setCfgError(null);

        const endpoint =
          typeGarde === "PR1"
            ? "/api/v2/config/desiderata-pr1"
            : typeGarde === "ASTREINTE_THORACIQUE"
            ? "/api/v2/config/desiderata-astreinte-thoracique"
            : typeGarde === "DIGESTIF"
            ? "/api/v2/config/desiderata-digestif"
            : typeGarde === "ORTHOPEDIE"
            ? "/api/v2/config/desiderata-orthopedie"
            : typeGarde === "INTERNE1"
            ? "/api/v2/config/desiderata-interne1"
            : typeGarde === "INTERNE2"
            ? "/api/v2/config/desiderata-interne2"
            : typeGarde === "REANIMATION"
            ? "/api/v2/config/desiderata-reanimation"
            : typeGarde === "INTERNE_DIGESTIF"
            ? "/api/v2/config/desiderata-interne-digestif"
            : typeGarde === "INTERNE_REA"
            ? "/api/v2/config/desiderata-interne-rea"
            : "/api/v2/config/desiderata-pr2";
        const res = await apiFetch(endpoint, { cache: "no-store" });
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} lecture config. ${t}`);
        }

        const json = (await res.json()) || {};
        setCfg(json);
      } catch (e) {
        console.error(`❌ Erreur chargement config desiderata ${typeGarde} :`, e);
        setCfgError(e.message || "Erreur inconnue");
        setCfg(null);
      } finally {
        setCfgLoading(false);
      }
    };

    loadCfg();
  }, [typeGarde, showPilotage]);

  const btnStyle = (variant) => {
    const base = {
      padding: "18px 20px",
      borderRadius: "14px",
      cursor: "pointer",
      fontWeight: 700,
      border: "1px solid transparent",
      fontSize: 18,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      boxShadow: "0 6px 14px rgba(0,0,0,0.08)",
      transition: "transform 0.15s, box-shadow 0.15s, background 0.15s",
      background: "#1f2937",
      color: "#fff",
    };

    switch (variant) {
      case "light":
        return {
          ...base,
          background: "#111827",
          color: "#f9fafb",
        };
      case "outline":
        return {
          ...base,
          background: "#ffffff",
          color: "#111827",
          border: "1px solid #d1d5db",
          boxShadow: "0 4px 10px rgba(0,0,0,0.04)",
        };
      default:
        return base;
    }
  };

  const cfgStatus = useMemo(() => {
    if (!cfg) return null;

    // ✅ Vérifier d'abord si mois_ouverts est vide (désidératas fermés)
    const moisOuverts = Array.isArray(cfg.mois_ouverts) ? cfg.mois_ouverts : [];
    if (moisOuverts.length === 0) {
      return { statusText: "FERMÉ (aucun mois ouvert)", statusVariant: "locked" };
    }

    const deadlineIso = cfg.deadline_iso ? String(cfg.deadline_iso) : null;
    const now = new Date();
    const deadline = deadlineIso ? new Date(deadlineIso) : null;

    // Optionnel : si tu ajoutes plus tard is_open côté backend, on le respecte
    const hasIsOpen = Object.prototype.hasOwnProperty.call(cfg, "is_open");
    const isOpenFlag = hasIsOpen ? !!cfg.is_open : null;

    // Sans is_open : on déduit simplement via la date
    const openByDate = deadline ? now <= deadline : null;

    let statusText = "—";
    let statusVariant = "neutral";

    if (hasIsOpen) {
      statusText = isOpenFlag ? "OUVERT" : "FERMÉ";
      statusVariant = isOpenFlag ? "open" : "locked";
    } else if (openByDate !== null) {
      statusText = openByDate ? `OUVERT (${moisOuverts.length} mois)` : "VERROUILLÉ (date passée)";
      statusVariant = openByDate ? "open" : "locked";
    }

    return { statusText, statusVariant };
  }, [cfg]);

  const pillStyle = (variant) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 9999,
    fontWeight: 800,
    fontSize: 13,
    letterSpacing: 0.4,
    background:
      variant === "locked"
        ? "rgba(220, 38, 38, 0.10)"
        : variant === "open"
        ? "rgba(16, 185, 129, 0.12)"
        : "rgba(59, 130, 246, 0.10)",
    color:
      variant === "locked"
        ? "#b91c1c"
        : variant === "open"
        ? "#065f46"
        : "#1d4ed8",
    border:
      variant === "locked"
        ? "1px solid rgba(220,38,38,0.25)"
        : variant === "open"
        ? "1px solid rgba(16,185,129,0.25)"
        : "1px solid rgba(59,130,246,0.25)",
    whiteSpace: "nowrap",
  });

  // ✅ Mois de référence : le plus récent entre la config desiderata et le mois courant
  const moisRef = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const moisCourant = `${y}-${m}`;
    if (cfg?.mois_cle) {
      // Prendre le plus récent entre le mois de la config et le mois courant
      return String(cfg.mois_cle) > moisCourant ? String(cfg.mois_cle) : moisCourant;
    }
    return moisCourant;
  }, [cfg]);

  const moisLabel = (mois) => {
    try {
      const [yStr, mStr] = String(mois).split("-");
      const y = parseInt(yStr, 10);
      const m = parseInt(mStr, 10);
      const d = new Date(y, m - 1, 1);
      let s = new Intl.DateTimeFormat("fr-FR", {
        month: "long",
        year: "numeric",
      }).format(d);
      return s.charAt(0).toUpperCase() + s.slice(1);
    } catch {
      return String(mois);
    }
  };

  const addMonths = (mois, delta) => {
    const [yStr, mStr] = String(mois).split("-");
    const y = parseInt(yStr, 10);
    const m = parseInt(mStr, 10);
    const d = new Date(y, m - 1, 1);
    d.setMonth(d.getMonth() + delta);
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${yy}-${mm}`;
  };

  // ✅ Vérifie si la liste validée existe via l'endpoint dédié est_validee (GET JSON)
  // Accepte un typeGarde (PR1, PR2, ASTREINTE_THORACIQUE) pour vérifier la bonne liste
  const checkValidee = async (mois, type = "PR2") => {
    try {
      const res = await apiFetch(`/api/v2/liste_gardes/est_validee/${mois}?type=${encodeURIComponent(type)}&_t=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return false;
      const json = await res.json();
      return !!(json.estValidee || json.valide);
    } catch {
      return false;
    }
  };

  const openMoisModal = async (mode) => {
    try {
      setMoisModalMode(mode);
      setShowMoisModal(true);
      setMoisLoading(true);
      setMoisErr(null);

      // Pour "avenir" : partir du mois courant (pas moisRef) et couvrir
      // assez de mois pour inclure moisRef + marge.
      // Cela garantit que tous les mois entre maintenant et les plus lointains
      // (ex: astreintes thoraciques avr→août) soient visibles.
      const d0 = new Date();
      const moisCourant = `${d0.getFullYear()}-${String(d0.getMonth() + 1).padStart(2, "0")}`;
      const startMois = mode === "avenir" ? moisCourant : moisRef;

      // Calculer le nombre de mois nécessaires
      // NOTE: pour le mode "précédents", on élargit volontairement la profondeur
      // afin de retrouver facilement des mois plus anciens (ex: janvier si moisRef=août/juillet).
      let count = mode === "avenir" ? 6 : 12;
      if (mode === "avenir" && moisRef > moisCourant) {
        // S'assurer de couvrir au moins moisRef + 5
        const [sy, sm] = moisCourant.split("-").map(Number);
        const [ry, rm] = moisRef.split("-").map(Number);
        const diff = (ry - sy) * 12 + (rm - sm);
        count = Math.max(count, diff + 6);
      }

      const deltas =
        mode === "avenir"
          ? Array.from({ length: count }, (_, i) => i) // courant, +1, +2...
          : Array.from({ length: count }, (_, i) => -i); // ref, -1, -2...

      const moisList = deltas.map((d) => addMonths(startMois, d));

      // Check en parallèle avec le type de garde actuel
      const statuses = await Promise.all(moisList.map((m) => checkValidee(m, typeGarde)));

      const items = moisList.map((m, idx) => ({
        mois: m,
        label: moisLabel(m),
        hasValidee: statuses[idx],
      }));

      setMoisItems(items);
    } catch (e) {
      console.error("❌ Erreur préparation sélecteur mois :", e);
      setMoisErr(e.message || "Erreur inconnue");
      setMoisItems([]);
    } finally {
      setMoisLoading(false);
    }
  };

  // Render helper, not a nested component: auth activity must not remount buttons during a click.
  const renderMoisModal = () => {
    if (!showMoisModal) return null;

    const title =
      moisModalMode === "avenir"
        ? "🔮 Gardes des mois à venir"
        : "📂 Gardes des mois précédents";

    const subtitle =
      moisModalMode === "avenir"
        ? "Accès rapide aux listes de garde (validées si elles existent)."
        : "Accès rapide aux listes des mois passés (validées si elles existent).";

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          zIndex: 9999,
        }}
        onClick={() => setShowMoisModal(false)}
      >
        <div
          style={{
            width: "min(680px, 100%)",
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid #e5e7eb",
            boxShadow: "0 24px 70px rgba(0,0,0,0.28)",
            padding: 18,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{title}</div>
              <div style={{ color: "#6b7280", marginTop: 4, fontSize: 13 }}>
                {subtitle}
              </div>
            </div>

            <button
              style={{
                padding: "8px 12px",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                background: "#fff",
                cursor: "pointer",
                fontWeight: 900,
              }}
              onClick={() => setShowMoisModal(false)}
              title="Fermer"
            >
              ✕
            </button>
          </div>

          <div style={{ marginTop: 14 }}>
            {moisLoading ? (
              <div style={{ color: "#6b7280" }}>Chargement…</div>
            ) : moisErr ? (
              <div style={{ color: "#b91c1c", fontWeight: 900 }}>❌ {moisErr}</div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {moisItems.map((it) => (
                  <div
                    key={it.mois}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 14,
                      padding: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      background: "#fff",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ fontWeight: 900, color: "#111827" }}>{it.label}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{it.mois}</div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span
                        style={{
                          ...pillStyle(it.hasValidee ? "open" : "locked"),
                          fontSize: 12,
                          padding: "6px 10px",
                        }}
                      >
                        {it.hasValidee ? "VALIDÉE" : "NON VALIDÉE"}
                      </span>

                      <button
                        style={{
                          padding: "10px 14px",
                          borderRadius: 12,
                          border: "1px solid #111827",
                          background: "#111827",
                          color: "#fff",
                          cursor: "pointer",
                          fontWeight: 900,
                        }}
                        onClick={() => {
                          setShowMoisModal(false);
                          navigate(
                            withCurrentBack(
                              it.hasValidee
                                ? `/responsable/gardes-valide?mois=${encodeURIComponent(
                                    it.mois
                                  )}&type=${encodeURIComponent(typeGarde)}`
                                : `/responsable/gardes-brute?mois=${encodeURIComponent(it.mois)}&type=${encodeURIComponent(typeGarde)}`
                            )
                          );
                        }}
                        title={
                          it.hasValidee
                            ? "Ouvrir la liste validée"
                            : "Ouvrir la liste brute (brouillon)"
                        }
                      >
                        Ouvrir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: 14,
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              style={{
                padding: "10px 14px",
                borderRadius: 12,
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: "pointer",
                fontWeight: 900,
              }}
              onClick={() => setShowMoisModal(false)}
            >
              Fermer
            </button>

            <button
              style={{
                padding: "10px 14px",
                borderRadius: 12,
                border: "1px solid #111827",
                background: "#111827",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 900,
              }}
              onClick={() => {
                const other = moisModalMode === "avenir" ? "precedents" : "avenir";
                openMoisModal(other);
              }}
            >
              {moisModalMode === "avenir" ? "Voir mois précédents" : "Voir mois à venir"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        maxWidth: "900px",
        margin: "40px auto",
        padding: "24px",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {renderMoisModal()}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 style={{ fontSize: "32px", fontWeight: 800, marginBottom: 8 }}>
            {typeGarde === "ASTREINTE_THORACIQUE" ? "🫁 Gestion des Astreintes thoraciques" : typeGarde === "PR1" ? "🩺 Gestion des Gardes Port Royal 1" : typeGarde === "DIGESTIF" ? "🏥 Gestion des Gardes Senior Digestif" : typeGarde === "ORTHOPEDIE" ? "🦴 Gestion des Astreintes Orthopédie" : typeGarde === "ORTHOPEDIE_JUNIOR" ? "🦴 Gestion de l’Astreinte junior Orthopédie" : typeGarde === "INTERNE1" ? "🩺 Gestion des Gardes Interne Port Royal 1" : typeGarde === "INTERNE2" ? "🩺 Gestion des Gardes Interne Port Royal 2" : typeGarde === "REANIMATION" ? "🏥 Gestion des Gardes Réanimation" : typeGarde === "INTERNE_DIGESTIF" ? "🎓 Gestion des Gardes Interne Digestif" : typeGarde === "INTERNE_REA" ? "🎓 Gestion des Gardes Interne Réanimation" : "🩺 Gestion des Gardes Port Royal 2"}
          </h1>
          <p style={{ color: "#374151", marginBottom: 14 }}>
            {typeGarde === "ASTREINTE_THORACIQUE"
              ? "Choisissez une section pour gérer les astreintes thoraciques du service."
              : typeGarde === "DIGESTIF"
              ? "Gestion des gardes senior viscérales (Digestif). Les désidérata sont gérés côté Digestif."
              : typeGarde === "ORTHOPEDIE"
              ? "Gestion des astreintes orthopédie. Les désidérata sont gérés séparément."
              : typeGarde === "ORTHOPEDIE_JUNIOR"
              ? "Saisie directe de l’astreinte junior Orthopédie, limitée aux samedis, dimanches et jours fériés."
              : typeGarde === "INTERNE1"
              ? "Gestion des gardes Interne Port Royal 1."
              : typeGarde === "INTERNE2"
              ? "Gestion des gardes Interne Port Royal 2."
              : typeGarde === "REANIMATION"
              ? "Gestion des gardes de réanimation."
              : typeGarde === "INTERNE_DIGESTIF"
              ? "Gestion des gardes Interne Anesthésie Digestif."
              : typeGarde === "INTERNE_REA"
              ? "Gestion des gardes Interne Réanimation."
              : "Choisissez une section pour gérer les gardes du service."}
          </p>
        </div>
        <LogoutButton />
      </div>

      {juniorCampaign && canManageJuniorCampaign(user, juniorCampaign.type) && (
        <div style={{ marginBottom: 18 }}>
          <button
            style={btnStyle("outline")}
            onClick={() => navigate(withCurrentBack(withScopedLock(`/responsable/gardes?type=${juniorCampaign.type}`)))}
          >
            {juniorCampaign.label}
          </button>
        </div>
      )}

      {juniorCampaign && !canManageJuniorCampaign(user, juniorCampaign.type) &&
        user?.junior_list_permissions?.[juniorCampaign.type]?.can_withdraw && (
        <div style={{ marginBottom: 18 }}>
          <button
            style={btnStyle("outline")}
            onClick={() => navigate(withCurrentBack(withScopedLock(`/responsable/gardes?type=${juniorCampaign.type}`)))}
          >
            Listes de garde des internes Digestif
          </button>
        </div>
      )}

      {/* ✅ Bloc “pilotage desiderata médecins” */}
      {showPilotage && (
      <div
        style={{
          border: "1px solid #e5e7eb",
          background: "#ffffff",
          borderRadius: 16,
          padding: 16,
          marginBottom: 18,
          boxShadow: "0 6px 16px rgba(0,0,0,0.04)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#111827" }}>
              ⚙️ Pilotage des désidérata (médecins)
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
              Définir le mois et la date limite affichés côté médecins.
            </div>
          </div>

          {cfgStatus ? (
            <span style={pillStyle(cfgStatus.statusVariant)}>{cfgStatus.statusText}</span>
          ) : (
            <span style={pillStyle("neutral")}>STATUT : —</span>
          )}
        </div>

        <div style={{ fontSize: 14, color: "#111827", lineHeight: 1.45 }}>
          {cfgLoading ? (
            <div style={{ color: "#6b7280" }}>Chargement de la configuration…</div>
          ) : cfgError ? (
            <div style={{ color: "#b91c1c", fontWeight: 800 }}>
              ❌ Impossible de lire la config : {cfgError}
            </div>
          ) : (Array.isArray(cfg?.mois_ouverts) && cfg.mois_ouverts.length === 0) ? (
            <div style={{ color: "#92400e", fontWeight: 800 }}>
              🔒 Les désidératas sont actuellement <b>FERMÉS</b> (aucun mois ouvert).
              <br />
              <span style={{ fontWeight: 400, color: "#6b7280" }}>
                Les médecins ne peuvent pas saisir leurs désidératas. Cliquez sur "Configurer" pour ouvrir un mois.
              </span>
            </div>
          ) : (
            <>
              <div>
                <b>Mois ouvert{(cfg?.mois_ouverts?.length || 0) > 1 ? "s" : ""} :</b>{" "}
                {cfg?.mois_ouverts?.map((m, i) => (
                  <span key={m.mois_cle || i}>
                    {i > 0 ? ", " : ""}
                    {m.mois_label || m.mois_cle}
                  </span>
                )) || cfg?.mois_label || "—"}
              </div>
              <div>
                <b>Date limite (mois principal) :</b>{" "}
                {cfg?.deadline_affichage || "—"}{" "}
                <span style={{ color: "#6b7280" }}>
                  (ISO: {cfg?.deadline_iso || "—"})
                </span>
              </div>
            </>
          )}
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button
            style={btnStyle("outline")}
            onClick={() => {
              // Navigation vers la page de config selon le type de garde
              const configPaths = {
                PR1: "/responsable/config-desiderata-pr1",
                PR2: "/responsable/config-desiderata",
                ASTREINTE_THORACIQUE: "/responsable/config-desiderata-astreinte-thoracique",
                ORTHOPEDIE: "/responsable/config-desiderata-orthopedie",
                OPH: "/responsable/config-desiderata-oph",
                INTERNE1: "/responsable/config-desiderata-interne1",
                INTERNE2: "/responsable/config-desiderata-interne2",
                DIGESTIF: "/responsable/config-desiderata-digestif",
                REANIMATION: "/responsable/config-desiderata-reanimation",
                INTERNE_DIGESTIF: "/responsable/config-desiderata-interne-digestif",
                INTERNE_REA: "/responsable/config-desiderata-interne-rea",
              };
              navigate(withCurrentBack(withScopedLock(configPaths[typeGarde] || "/responsable/config-desiderata")));
            }}
          >
            ⚙️ Configurer / Ouvrir les désidérata médecins ({typeGarde})
          </button>

        </div>
      </div>
      )}

      {/* 🔹 Menu "façon planning" — libellés "Astreinte(s)" en contexte astreinte thoracique */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        <button
          style={btnStyle()}
          onClick={() => {
            // "Mois en cours" = le vrai mois calendaire, pas le mois de la config desiderata
            const now = new Date();
            const moisEnCours = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
            navigate(withCurrentBack(`/responsable/gardes-valide?mois=${encodeURIComponent(moisEnCours)}&type=${encodeURIComponent(typeGarde)}`));
          }}
        >
          📆 {isAstreinteType ? "Astreinte du mois en cours" : "Gardes du mois en cours"}
        </button>

        {!isDirectSaisieType && (
        <button
          style={btnStyle()}
          onClick={() => {
            navigate(withCurrentBack(`/responsable/gardes-desiderata?type=${typeGarde}`));
          }}
        >
          ✍️ {typeGarde === "ASTREINTE_THORACIQUE" ? "Astreinte – désidérata" : "Gardes – désidérata"}
        </button>
        )}

        <button
          style={btnStyle("light")}
          onClick={() => openMoisModal("precedents")}
        >
          📂 {isAstreinteType ? "Astreintes des mois précédents" : "Gardes des mois précédents"}
        </button>

        <button
          style={btnStyle("light")}
          onClick={() => openMoisModal("avenir")}
        >
          🔮 {isAstreinteType ? "Astreintes des mois à venir" : "Gardes des mois à venir"}
        </button>
      </div>

      {/* 🔹 Liste des médecins — conservé uniquement pour les listes internes non reprises dans le hub secteur */}
      {!hideListeMedecinsBlock && (
        <div
          style={{
            border: "1px solid #e5e7eb",
            background: "#ffffff",
            borderRadius: 16,
            padding: 16,
            marginBottom: 18,
            boxShadow: "0 6px 16px rgba(0,0,0,0.04)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 16, fontWeight: 900, color: "#111827" }}>
                {typeGarde === "INTERNE1" ? "👥 Liste des médecins Gardes Interne Port Royal 1" : typeGarde === "INTERNE2" ? "👥 Liste des médecins Gardes Interne Port Royal 2" : typeGarde === "INTERNE_DIGESTIF" ? "👥 Liste des internes Gardes Anesthésie Digestif" : typeGarde === "INTERNE_REA" ? "👥 Liste des internes Gardes Réanimation" : typeGarde === "ORTHOPEDIE_JUNIOR" ? "👥 Liste des médecins Astreinte junior Orthopédie" : "👥 Liste des médecins Gardes"}
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                {typeGarde === "INTERNE1" ? "Sélectionnez les médecins concernés par les gardes Interne Port Royal 1." : typeGarde === "INTERNE2" ? "Sélectionnez les médecins concernés par les gardes Interne Port Royal 2." : typeGarde === "ORTHOPEDIE_JUNIOR" ? "Sélectionnez les médecins juniors concernés par l’astreinte junior Orthopédie." : "Sélectionnez les médecins concernés par les gardes."}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <button
              style={btnStyle("outline")}
              onClick={() => {
                navigate(
                  withScopedLock(withCurrentBack(
                    typeGarde
                      ? `/responsable/gardes-liste-medecins?type=${encodeURIComponent(typeGarde)}`
                      : "/responsable/gardes-liste-medecins"
                  ))
                );
              }}
            >
              {typeGarde === "INTERNE1" ? "👥 Gérer la liste des médecins Gardes Interne Port Royal 1" : typeGarde === "INTERNE2" ? "👥 Gérer la liste des médecins Gardes Interne Port Royal 2" : typeGarde === "ORTHOPEDIE_JUNIOR" ? "👥 Gérer la liste Astreinte junior Orthopédie" : "👥 Gérer la liste des médecins Gardes"}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
